import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { IStore } from '../../../interfaces/StoreTypes';
import playerService from '../../../helpers/bitmovinPlayer';
import { safePlayerPlay } from '../../../helpers/playerGuards';
import { useUpgradedModalState } from '../../../hooks/usePPVEvents';
import { getNoAdCtaAdBreakStartTime, getOutstreamPlayerForNoAdCta } from '../utils/AdsUiUtils';

/**
 * Resumes ad playback after the upgrade modal (opened via the No Ads CTA) is closed.
 *
 * Session-storage keys consumed:
 * - `noAdCta_wasLongStay` – `"1"` when the user had a long stay on the upgrade screen.
 */
export default function useResumeAdPlayback() {
    const { showUpgradedModal: isUpgradeModalOpen } = useUpgradedModalState();
    const upgradeModalFromNoAdsCta = useSelector(
        (state: IStore) => state?.generalConfiguration?.upgradeModalFromNoAdsCta ?? false
    );

    const lastUpgradeModalOpenRef = useRef(false);

    /** True while upgrade modal is open from No Ads CTA; consumed when modal closes to resume ad playback */
    const pendingNoAdsCtaResumeRef = useRef(false);

    useEffect(() => {
        if (isUpgradeModalOpen && upgradeModalFromNoAdsCta) {
            pendingNoAdsCtaResumeRef.current = true;
        }
    }, [isUpgradeModalOpen, upgradeModalFromNoAdsCta]);

    useEffect(() => {
        const wasOpen = lastUpgradeModalOpenRef.current;
        lastUpgradeModalOpenRef.current = isUpgradeModalOpen;

        if (!wasOpen || isUpgradeModalOpen || !pendingNoAdsCtaResumeRef.current) {
            return;
        }

        pendingNoAdsCtaResumeRef.current = false;
        const wasLongStay = sessionStorage.getItem('noAdCta_wasLongStay') === '1';
        sessionStorage.removeItem('noAdCta_wasLongStay');

        // TOOD: should player be passed as argument?
        let player;
        player = playerService.player;

        if (!player) {
            player = getOutstreamPlayerForNoAdCta();
        }

        if (!player) return;

        if (wasLongStay) {
            const restartTime = getNoAdCtaAdBreakStartTime() ?? 0;
            try {
                player.seek(restartTime);
            } catch {
                // ignore
            }
                safePlayerPlay(player);
            } else {
                // Short stay: resume from where the ad was paused
                safePlayerPlay(player);
            }
    }, [isUpgradeModalOpen]);
}
